import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, qs, qsa, onClick, toast } from '../dom.js';
import { icon } from '../icons.js';
import { loadLayout } from '../../forms.js';
import { ScanController } from '../scanController.js';

export async function answerKeyView(params: Record<string, string>): Promise<RenderedView> {
  const quiz = await repos.getQuiz(params.id);
  if (!quiz) return { title: 'Answer key', back: '/quizzes', html: `<div class="view"><p class="empty">Quiz not found.</p></div>` };
  const [layout, existing] = await Promise.all([loadLayout(quiz.items), repos.answerKeyFor(quiz.id)]);
  // TESTIFY keys are locked (§13.3): pulled pre-hashed, never edited or scanned.
  if (quiz.origin === 'testify' || existing?.locked) {
    return {
      title: 'Answer key',
      subtitle: quiz.title,
      back: `/quiz/${quiz.id}`,
      html: `<div class="view"><div class="card"><span class="badge badge-muted">${icon.key()} Locked — from TESTIFY</span>
        <p style="margin:12px 0 0">This exam's answer key comes from TESTIFY (hashed) and can't be edited or re-scanned here. Papers are scored against it automatically; TESTIFY re-scores on upload.</p></div></div>`,
    };
  }
  const opts = layout.quiz.options;
  let ctrl: ScanController | null = null;

  const save = async (answers: Record<number, string>) => {
    if (!Object.keys(answers).length) return toast('Set at least one answer', 'err');
    await repos.setAnswerKey(quiz.id, answers);
    toast('Answer key saved');
    navigate(`#/quiz/${quiz.id}`);
  };

  const renderScan = (root: HTMLElement) => {
    ctrl?.dispose();
    const host = qs(root, '#keyroot')!;
    ctrl = new ScanController(host, {
      layout,
      mode: 'key',
      hint: 'Scan the filled answer-key sheet. Confirm any flagged item before saving.',
      onSave: async (raw) => {
        const answers: Record<number, string> = {};
        for (const a of raw.answers) if (a.chosen) answers[a.q] = a.chosen;
        await save(answers);
      },
    });
    ctrl.start();
  };

  const renderManual = (root: HTMLElement) => {
    ctrl?.dispose();
    ctrl = null;
    const answers: Record<number, string> = { ...(existing?.answers ?? {}) };
    const host = qs(root, '#keyroot')!;
    host.innerHTML = `<p class="hint" id="cnt">${Object.keys(answers).length} / ${quiz.items} set</p>
      <div class="answers-grid">${Array.from({ length: quiz.items }, (_, i) => {
        const q = i + 1;
        return `<div class="qrow" data-row="${q}"><span class="qn">${q}</span><span class="opts">${opts
          .map((o) => `<button type="button" class="opt ${answers[q] === o ? 'on' : ''}" data-q="${q}" data-opt="${esc(o)}">${esc(o)}</button>`)
          .join('')}</span></div>`;
      }).join('')}</div>
      <div class="actionbar"><button class="btn btn-primary btn-block" data-save>${icon.check()} Save key</button></div>`;
    host.querySelector('.answers-grid')!.addEventListener('click', (e) => {
      const el = (e.target as HTMLElement).closest('.opt') as HTMLElement | null;
      if (!el) return;
      const q = Number(el.dataset.q);
      answers[q] = answers[q] === el.dataset.opt ? (delete answers[q], undefined as any) : el.dataset.opt!;
      qsa(host, `.opt[data-q="${q}"]`).forEach((b) => b.classList.toggle('on', (b as HTMLElement).dataset.opt === answers[q]));
      qs(host, '#cnt')!.textContent = `${Object.keys(answers).length} / ${quiz.items} set`;
    });
    onClick(host, '[data-save]', () => save(answers));
  };

  return {
    title: 'Answer key',
    subtitle: quiz.title,
    back: `/quiz/${quiz.id}`,
    html: `<div class="view view-flush">
      <div class="tabs"><button data-tab="scan" class="on">${icon.camera()} Scan key</button><button data-tab="manual">${icon.edit()} Manual</button></div>
      <div id="keyroot"></div></div>`,
    unmount: () => ctrl?.dispose(),
    mount(root) {
      const setTab = (name: string) => {
        qsa(root, '.tabs button').forEach((b) => b.classList.toggle('on', b.dataset.tab === name));
        if (name === 'scan') renderScan(root);
        else renderManual(root);
      };
      onClick(root, '.tabs button', (el) => setTab(el.dataset.tab!));
      setTab('scan');
    },
  };
}
